import type { ReactNode } from 'react';
import { T } from 'gt-react';
import { WarningCircleIcon } from '@phosphor-icons/react';

import { ReasonChips } from './ReasonChips';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type {
  FlaggedPotfUpcRow,
  FlaggedVendorItemListRow,
  FlaggedWebNoPriceRow,
} from '@/lib/daily-reports/types';

function WorklistBanner({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-50/50 p-3 text-sm dark:bg-amber-950/20">
      <WarningCircleIcon
        className="mt-0.5 h-4 w-4 shrink-0 text-amber-600"
        weight="fill"
      />
      <p>{children}</p>
    </div>
  );
}

export function WebNoPriceTable({ rows }: { rows: FlaggedWebNoPriceRow[] }) {
  if (rows.length === 0) return null;
  return (
    <div className="flex flex-col gap-3">
      <WorklistBanner>
        <T context="WEBNOPRF rows are web-flagged items with no price record">
          Web-flagged item with no price record. Add a price in AS/400. Gently
          does not write back to AS/400.
        </T>
      </WorklistBanner>
      <div className="overflow-x-auto rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                <T context="column header for the source daily report file name">
                  File
                </T>
              </TableHead>
              <TableHead>
                <T context="column header for WEBNOPRF worksheet name">Sheet</T>
              </TableHead>
              <TableHead>
                <T context="column header for DIV on a WEBNOPRF row">DIV</T>
              </TableHead>
              <TableHead>
                <T context="column header for product number on a WEBNOPRF row">
                  Product
                </T>
              </TableHead>
              <TableHead>
                <T context="column header for description on a WEBNOPRF row">
                  Description
                </T>
              </TableHead>
              <TableHead>
                <T context="column header listing why a WEBNOPRF row was flagged">
                  Reasons
                </T>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow
                key={`${row.fileName}-${row.sheetName}-${row.sheetRow}-${row.productNumber}`}
              >
                <TableCell className="max-w-[10rem] truncate text-xs">
                  {row.fileName}
                </TableCell>
                <TableCell className="max-w-[10rem] truncate text-xs">
                  {row.sheetName}
                </TableCell>
                <TableCell>{row.div}</TableCell>
                <TableCell className="font-medium">
                  {row.productNumber}
                </TableCell>
                <TableCell className="max-w-[16rem] truncate">
                  {row.description}
                </TableCell>
                <TableCell>
                  <ReasonChips reasons={row.reasons} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export function PotfUpcTable({ rows }: { rows: FlaggedPotfUpcRow[] }) {
  if (rows.length === 0) return null;
  return (
    <div className="flex flex-col gap-3">
      <WorklistBanner>
        <T context="POTF_UPC rows are POTF items that already have a UPC code">
          POTF item with a UPC code. Review in AS/400. Gently does not write
          back to AS/400.
        </T>
      </WorklistBanner>
      <div className="overflow-x-auto rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                <T context="column header for the source daily report file name">
                  File
                </T>
              </TableHead>
              <TableHead>
                <T context="column header for POTF_UPC worksheet name">Sheet</T>
              </TableHead>
              <TableHead>
                <T context="column header for product number on a POTF_UPC row">
                  Product
                </T>
              </TableHead>
              <TableHead>
                <T context="column header for UPC CODE on a POTF_UPC row">
                  UPC
                </T>
              </TableHead>
              <TableHead>
                <T context="column header for other catalog part numbers sharing this UPC">
                  Other Part#
                </T>
              </TableHead>
              <TableHead>
                <T context="column header for catalog sources of other parts sharing this UPC">
                  Other Divisions
                </T>
              </TableHead>
              <TableHead>
                <T context="column header for description on a POTF_UPC row">
                  Description
                </T>
              </TableHead>
              <TableHead>
                <T context="column header listing why a POTF_UPC row was flagged">
                  Reasons
                </T>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow
                key={`${row.fileName}-${row.sheetName}-${row.sheetRow}-${row.productNumber}`}
              >
                <TableCell className="max-w-[10rem] truncate text-xs">
                  {row.fileName}
                </TableCell>
                <TableCell className="max-w-[10rem] truncate text-xs">
                  {row.sheetName}
                </TableCell>
                <TableCell className="font-medium">
                  {row.productNumber}
                </TableCell>
                <TableCell>{row.upcCode}</TableCell>
                <TableCell className="font-medium">
                  {row.otherPartNumber}
                </TableCell>
                <TableCell>{row.otherDivisions}</TableCell>
                <TableCell className="max-w-[16rem] truncate">
                  {row.description}
                </TableCell>
                <TableCell>
                  <ReasonChips reasons={row.reasons} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export function VendorItemListTable({
  rows,
}: {
  rows: FlaggedVendorItemListRow[];
}) {
  if (rows.length === 0) return null;
  return (
    <div className="flex flex-col gap-3">
      <WorklistBanner>
        <T context="VITMLISTER rows have a pipe in an English or French description">
          Pipe in English or French description. Download the change upload to
          replace each pipe with a dash. Gently does not write back to AS/400.
        </T>
      </WorklistBanner>
      <div className="overflow-x-auto rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                <T context="column header for the source daily report file name">
                  File
                </T>
              </TableHead>
              <TableHead>
                <T context="column header for VITMLISTER worksheet name">
                  Sheet
                </T>
              </TableHead>
              <TableHead>
                <T context="column header for product number on a VITMLISTER row">
                  Product
                </T>
              </TableHead>
              <TableHead>
                <T context="column header for vendor name on a VITMLISTER row">
                  Vendor
                </T>
              </TableHead>
              <TableHead>
                <T context="column header for English description on a VITMLISTER row">
                  English
                </T>
              </TableHead>
              <TableHead>
                <T context="column header for French description on a VITMLISTER row">
                  French
                </T>
              </TableHead>
              <TableHead>
                <T context="column header listing why a VITMLISTER row was flagged">
                  Reasons
                </T>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow
                key={`${row.fileName}-${row.sheetName}-${row.sheetRow}-${row.productNumber}`}
              >
                <TableCell className="max-w-[10rem] truncate text-xs">
                  {row.fileName}
                </TableCell>
                <TableCell className="max-w-[10rem] truncate text-xs">
                  {row.sheetName}
                </TableCell>
                <TableCell className="font-medium">
                  {row.productNumber}
                </TableCell>
                <TableCell className="max-w-[12rem] truncate">
                  {row.vendorName}
                </TableCell>
                <TableCell className="max-w-[16rem] truncate">
                  {row.englishDescription}
                </TableCell>
                <TableCell className="max-w-[16rem] truncate">
                  {row.frenchDescription}
                </TableCell>
                <TableCell>
                  <ReasonChips reasons={row.reasons} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
